import React, { useState, useEffect } from 'react';
import { useCreateTransaction, useUpdateTransaction } from '../../hooks/useTransactions';
import { useAccounts } from '../../hooks/useAccounts';
import { useToast } from '../../lib/toast';
import type { TransactionType, Transaction } from '../../types/transaction';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { formatCurrency } from '../../lib/currency';

interface TransactionFormProps {
    onSuccess?: () => void;
    initialData?: Transaction;
    mode?: 'create' | 'edit';
}

const categories = {
    income: ['Salary', 'Freelance', 'Investment', 'Gift', 'Other'],
    expense: ['Rent', 'Food', 'Transport', 'Shopping', 'Bills', 'Healthcare', 'Entertainment', 'Other'],
};

export function TransactionForm({ onSuccess, initialData, mode = 'create' }: TransactionFormProps) {
    const [type, setType] = useState<TransactionType>(initialData?.type || 'expense');
    const [accountId, setAccountId] = useState(initialData?.account_id.toString() || '');
    const [amount, setAmount] = useState(initialData?.amount.toString() || '');
    const [category, setCategory] = useState(initialData?.category || '');
    const [description, setDescription] = useState(initialData?.description || '');
    const [transactionDate, setTransactionDate] = useState(
        initialData?.transaction_date || new Date().toISOString().split('T')[0]
    );

    const { data: accounts } = useAccounts();
    const createTransaction = useCreateTransaction();
    const updateTransaction = useUpdateTransaction(initialData?.id || 0);
    const toast = useToast();

    // Filter out archived accounts
    const activeAccounts = accounts?.filter(acc => acc.is_active) || [];

    useEffect(() => {
        if (initialData) {
            setType(initialData.type);
            setAccountId(initialData.account_id.toString());
            setAmount(initialData.amount.toString());
            setCategory(initialData.category || '');
            setDescription(initialData.description || '');
            setTransactionDate(initialData.transaction_date);
        }
    }, [initialData]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!accountId || !amount || !transactionDate) {
            toast.error('Please fill in all required fields');
            return;
        }

        const transactionData = {
            account_id: parseInt(accountId),
            type,
            amount: parseFloat(amount),
            category: category || undefined,
            description: description || undefined,
            transaction_date: transactionDate,
        };

        try {
            if (mode === 'edit') {
                await updateTransaction.mutateAsync(transactionData);
                toast.success('Transaction updated successfully');
            } else {
                await createTransaction.mutateAsync(transactionData);
                toast.success('Transaction created successfully');

                // Reset form only in create mode
                setAccountId('');
                setAmount('');
                setCategory('');
                setDescription('');
                setTransactionDate(new Date().toISOString().split('T')[0]);
            }

            onSuccess?.();
        } catch (err) {
            toast.error(`Failed to ${mode === 'edit' ? 'update' : 'create'} transaction`);
        }
    };

    const currentCategories = categories[type as keyof typeof categories] || [];

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type <span className="text-red-500">*</span>
                </label>
                <input type="hidden" name="type" value={type} />
                <div className="grid grid-cols-2 gap-2">
                    <button
                        type="button"
                        onClick={() => {
                            setType('income');
                            setCategory('');
                        }}
                        className={`p-3 rounded-lg border-2 transition-all ${
                            type === 'income'
                                ? 'border-green-500 bg-green-50 text-green-700'
                                : 'border-gray-200 hover:border-gray-300'
                        }`}
                    >
                        Income
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            setType('expense');
                            setCategory('');
                        }}
                        className={`p-3 rounded-lg border-2 transition-all ${
                            type === 'expense'
                                ? 'border-red-500 bg-red-50 text-red-700'
                                : 'border-gray-200 hover:border-gray-300'
                        }`}
                    >
                        Expense
                    </button>
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Account <span className="text-red-500">*</span>
                </label>
                <select
                    name="account_id"
                    value={accountId}
                    onChange={(e) => setAccountId(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                >
                    <option value="">Select an account</option>
                    {activeAccounts.map((account) => (
                        <option key={account.id} value={account.id}>
                            {account.name} ({formatCurrency(account.balance)})
                        </option>
                    ))}
                </select>
            </div>

            <Input
                label="Amount"
                name="amount"
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                required
            />

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                </label>
                <select
                    name="category"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                    <option value="">Select a category</option>
                    {currentCategories.map((cat) => (
                        <option key={cat} value={cat}>
                            {cat}
                        </option>
                    ))}
                </select>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                </label>
                <textarea
                    name="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Optional note about this transaction"
                    rows={3}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
            </div>

            <Input
                label="Date"
                name="transaction_date"
                type="date"
                value={transactionDate}
                onChange={(e) => setTransactionDate(e.target.value)}
                required
            />

            <div className="flex justify-end gap-3 pt-2">
                <Button
                    type="submit"
                    variant="primary"
                    disabled={mode === 'edit' ? updateTransaction.isPending : createTransaction.isPending}
                >
                    {mode === 'edit'
                        ? (updateTransaction.isPending ? 'Updating...' : 'Update Transaction')
                        : (createTransaction.isPending ? 'Creating...' : 'Create Transaction')
                    }
                </Button>
            </div>
        </form>
    );
}
